import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Checkbox,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { toast } from "react-toastify";
import ApiService from "../../functions/apiService";

const RED = "#BF1D2D";

const AVATAR_PALETTE = [
  "#BF1D2D", "#8B5CF6", "#10B981", "#0EA5E9", "#F59E0B",
  "#EC4899", "#6366F1", "#14B8A6", "#F97316", "#0891B2",
];

const getAvatarColor = (seed = "") => {
  const str = String(seed || "U");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const getInitials = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/);
  if (!parts[0]) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Normalize a listing row coming from GET /custom-group into a stable shape.
// `value` is the SO_VALUE used by the detail / edit / delete endpoints.
const normalizeGroup = (g = {}) => ({
  raw: g,
  value: g.SO_VALUE ?? g.value ?? g.id ?? g.Id ?? g.So_Value,
  name: g.SO_DESC ?? g.name ?? g.Name ?? g.desc ?? "Untitled group",
  employees: g.employees ?? g.Employees ?? [],
});

const emptyForm = { name: "", employees: [] };

export default function CustomGroups() {
  const [groups, setGroups] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Create / edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'edit'
  const [editingValue, setEditingValue] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formLoading, setFormLoading] = useState(false); // prefill loading
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");

  // View members dialog
  const [viewGroup, setViewGroup] = useState(null);
  const [viewMembers, setViewMembers] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchGroups = () => {
    setLoading(true);
    ApiService.get("/custom-group")
      .then(({ data }) => {
        const list = data?.data ?? data ?? [];
        setGroups((Array.isArray(list) ? list : []).map(normalizeGroup));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Failed to load custom groups");
      });
  };

  const fetchEmployees = () => {
    ApiService.get("/employeeToSwap")
      .then(({ data }) => {
        const options = (data || []).map((e) => {
          const name =
            e.fullName || `${e.First_name || ""} ${e.Last_name || ""}`.trim();
          return {
            value: String(e.Emp_Code),
            desc:
              `${e.EA_Rank ? e.EA_Rank + " " : ""}${name}`.trim() ||
              String(e.Emp_Code),
          };
        });
        setEmployeeOptions(options);
      })
      .catch(() => toast.error("Failed to load employees"));
  };

  useEffect(() => {
    fetchGroups();
    fetchEmployees();
  }, []);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  // Map a list of employee codes/objects to the loaded option objects.
  const toEmployeeOptions = (employees = []) =>
    employees.map((e) => {
      const code = String(
        e?.value ?? e?.Emp_Code ?? e?.emp_code ?? e?.SO_VALUE ?? e ?? ""
      );
      const match = employeeOptions.find((o) => o.value === code);
      if (match) return match;
      const built =
        e?.desc ||
        e?.fullName ||
        e?.full_name ||
        e?.name ||
        e?.SO_DESC ||
        `${e?.First_name || ""} ${e?.Last_name || ""}`.trim();
      return { value: code, desc: built || code };
    });

  // Authoritative group detail (members) for prefilling the edit form.
  // Best-effort: the listing already carries members, so callers fall back to it.
  const loadGroupDetail = (group) =>
    ApiService.get(`/custom-group/${group.value}`).then(({ data }) => {
      const detail = data?.data ?? data ?? {};
      const employees = detail.employees ?? detail.Employees ?? group.employees;
      return {
        name: detail.name ?? detail.SO_DESC ?? group.name ?? "",
        members: toEmployeeOptions(employees || []),
      };
    });

  // The listing response already includes each group's members, so render
  // the view straight from it — no dependency on the detail endpoint.
  const openView = (group) => {
    setViewGroup(group);
    setMemberSearch("");
    setViewMembers(toEmployeeOptions(group.employees || []));
    setViewLoading(false);
  };

  const openCreate = () => {
    setDialogMode("create");
    setEditingValue(null);
    setForm(emptyForm);
    setNameError("");
    setDialogOpen(true);
  };

  const openEdit = (group) => {
    setDialogMode("edit");
    setEditingValue(group.value);
    setNameError("");
    setForm({ name: group.name, employees: toEmployeeOptions(group.employees) });
    setDialogOpen(true);

    // Try to enrich with authoritative detail; if it fails, keep the listing
    // data we already populated above (no error surfaced to the user).
    setFormLoading(true);
    loadGroupDetail(group)
      .then(({ name, members }) => {
        setForm({ name, employees: members });
      })
      .catch(() => {})
      .finally(() => setFormLoading(false));
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleSave = () => {
    const name = form.name.trim();
    if (!name) {
      setNameError("Group name is required");
      return;
    }
    if (form.employees.length === 0) {
      toast.error("Select at least one employee");
      return;
    }

    setSaving(true);
    const body = {
      name,
      employees: form.employees.map((e) => e.value),
    };

    const request =
      dialogMode === "edit"
        ? ApiService.post(`/custom-group/${editingValue}`, body)
        : ApiService.post("/custom-group", body);

    request
      .then((res) => {
        setSaving(false);
        setDialogOpen(false);
        toast.success(
          res?.message ||
            (dialogMode === "edit" ? "Group updated" : "Group created")
        );
        fetchGroups();
      })
      .catch(({ data } = {}) => {
        setSaving(false);
        toast.error(data?.message || "Failed to save group");
      });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    ApiService.post(`/delete-custom-group/${deleteTarget.value}`, {})
      .then((res) => {
        setDeleting(false);
        setDeleteTarget(null);
        toast.success(res?.message || "Group deleted");
        fetchGroups();
      })
      .catch(({ data } = {}) => {
        setDeleting(false);
        toast.error(data?.message || "Failed to delete group");
      });
  };

  const filteredViewMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return viewMembers;
    return viewMembers.filter((m) => (m.desc || "").toLowerCase().includes(q));
  }, [viewMembers, memberSearch]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "#F9FAFB", minHeight: "100%" }}>
      {/* Header banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: "18px",
          background: "linear-gradient(135deg, #BF1D2D 0%, #7F1420 100%)",
          color: "#fff",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.3px" }}>
              Custom Groups
            </Typography>
            <Typography sx={{ fontSize: 13.5, opacity: 0.9 }}>
              {groups.length} group{groups.length === 1 ? "" : "s"} · create reusable recipient lists for messaging
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            bgcolor: "#fff",
            color: RED,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            px: 2.5,
            height: 44,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            "&:hover": { bgcolor: "#FFF1F2", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" },
          }}
        >
          New Group
        </Button>
      </Paper>

      {/* Toolbar: list heading + search */}
      {!loading && groups.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2.5,
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
            All Groups{" "}
            <Box component="span" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
              ({filteredGroups.length})
            </Box>
          </Typography>

          <TextField
            size="small"
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Body */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: RED }} />
        </Box>
      ) : filteredGroups.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px dashed #D1D5DB",
            borderRadius: "16px",
            py: 8,
            textAlign: "center",
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "#F3F4F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 30, color: "#9CA3AF" }} />
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 0.5 }}>
            {search ? "No groups match your search" : "No custom groups yet"}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6B7280", mb: 3 }}>
            {search
              ? "Try a different search term."
              : "Create your first custom group to quickly message a set of people."}
          </Typography>
          {!search && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: RED,
                color: RED,
                borderRadius: "10px",
                "&:hover": { borderColor: "#A81826", bgcolor: "rgba(191,29,45,0.04)" },
              }}
            >
              New Group
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {filteredGroups.map((group) => {
            const count = Array.isArray(group.employees)
              ? group.employees.length
              : 0;
            const preview = toEmployeeOptions(
              Array.isArray(group.employees) ? group.employees.slice(0, 4) : []
            );
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={group.value}>
                <Paper
                  elevation={0}
                  onClick={() => openView(group)}
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid #E5E7EB",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    bgcolor: "#fff",
                    transition: "box-shadow .2s, transform .2s, border-color .2s",
                    "&:hover": {
                      boxShadow: "0 14px 32px rgba(17,24,39,0.13)",
                      transform: "translateY(-3px)",
                      borderColor: "rgba(191,29,45,0.35)",
                    },
                    "&:hover .card-actions": { opacity: 1 },
                    "&:hover .view-btn": { bgcolor: RED, color: "#fff" },
                  }}
                >
                  {/* Colored top accent */}
                  <Box sx={{ height: 4, bgcolor: getAvatarColor(group.name) }} />

                  {/* Header: avatar + actions */}
                  <Box sx={{ p: 2.5, pb: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        bgcolor: getAvatarColor(group.name),
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 18,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 6px 14px ${getAvatarColor(group.name)}55`,
                      }}
                    >
                      {getInitials(group.name)}
                    </Box>
                    <Box
                      className="card-actions"
                      sx={{ display: "flex", gap: 0.5, opacity: 0.45, transition: "opacity .2s" }}
                    >
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(group);
                          }}
                          sx={{ bgcolor: "#F3F4F6", color: "#6B7280", "&:hover": { color: RED, bgcolor: "rgba(191,29,45,0.1)" } }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(group);
                          }}
                          sx={{ bgcolor: "#F3F4F6", color: "#6B7280", "&:hover": { color: "#DC2626", bgcolor: "rgba(220,38,38,0.1)" } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Name + subtitle */}
                  <Box sx={{ px: 2.5, pt: 1.75 }}>
                    <Typography
                      sx={{
                        fontSize: 17,
                        fontWeight: 700,
                        color: "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={group.name}
                    >
                      {group.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#9CA3AF", mt: 0.25 }}>
                      Custom recipient group
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }} />

                  <Divider sx={{ mt: 2, borderColor: "#F3F4F6" }} />

                  {/* Footer: member preview + view button */}
                  <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                      {count > 0 ? (
                        <>
                          <AvatarGroup
                            max={4}
                            sx={{
                              "& .MuiAvatar-root": {
                                width: 26,
                                height: 26,
                                fontSize: 10.5,
                                fontWeight: 700,
                                border: "2px solid #fff",
                              },
                            }}
                          >
                            {preview.map((m) => (
                              <Avatar key={m.value} sx={{ bgcolor: getAvatarColor(m.desc) }}>
                                {getInitials(m.desc)}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                          <Typography sx={{ fontSize: 12.5, color: "#6B7280", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {count} member{count === 1 ? "" : "s"}
                          </Typography>
                        </>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#9CA3AF" }}>
                          <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>No members</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box
                      className="view-btn"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "8px",
                        bgcolor: "rgba(191,29,45,0.08)",
                        color: RED,
                        fontSize: 12,
                        fontWeight: 700,
                        transition: "background-color .2s, color .2s",
                        flexShrink: 0,
                      }}
                    >
                      View <ArrowForwardIosRoundedIcon sx={{ fontSize: 10 }} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* View members dialog */}
      <Dialog
        open={Boolean(viewGroup)}
        onClose={() => setViewGroup(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <Box
          sx={{
            p: 2.5,
            background: "linear-gradient(135deg, #BF1D2D 0%, #7F1420 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700 }}>
              {getInitials(viewGroup?.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {viewGroup?.name}
              </Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.9 }}>
                {viewLoading ? "Loading members…" : `${viewMembers.length} member${viewMembers.length === 1 ? "" : "s"}`}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setViewGroup(null)} size="small" sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {viewLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: RED }} />
            </Box>
          ) : viewMembers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: "#6B7280" }}>
              <PersonOutlineRoundedIcon sx={{ fontSize: 36, color: "#D1D5DB", mb: 1 }} />
              <Typography sx={{ fontSize: 14 }}>This group has no members yet.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, pb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search members..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Box>
              <Box sx={{ maxHeight: 360, overflowY: "auto", px: 1, pb: 1 }}>
                {filteredViewMembers.length === 0 ? (
                  <Typography sx={{ textAlign: "center", py: 3, color: "#9CA3AF", fontSize: 13 }}>
                    No members match "{memberSearch}"
                  </Typography>
                ) : (
                  filteredViewMembers.map((m) => (
                    <Box
                      key={m.value}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: "10px",
                        "&:hover": { bgcolor: "#F9FAFB" },
                      }}
                    >
                      <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: getAvatarColor(m.desc) }}>
                        {getInitials(m.desc)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {m.desc}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
                          ID: {m.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button
            startIcon={<DeleteOutlineIcon />}
            onClick={() => {
              const g = viewGroup;
              setViewGroup(null);
              setDeleteTarget(g);
            }}
            sx={{ textTransform: "none", color: "#DC2626", fontWeight: 600, mr: "auto" }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={() => {
              const g = viewGroup;
              setViewGroup(null);
              openEdit(g);
            }}
            sx={{
              bgcolor: RED,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": { bgcolor: "#A81826", boxShadow: "none" },
            }}
          >
            Edit Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                bgcolor: "rgba(191,29,45,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GroupsOutlinedIcon sx={{ color: RED, fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
              {dialogMode === "edit" ? "Edit Group" : "New Custom Group"}
            </Typography>
          </Box>
          <IconButton onClick={closeDialog} size="small" disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {formLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress sx={{ color: RED }} />
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 0.75 }}>
                Group name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. A Shift Captains"
                value={form.name}
                error={Boolean(nameError)}
                helperText={nameError}
                onChange={(e) => {
                  setForm((p) => ({ ...p, name: e.target.value }));
                  if (nameError) setNameError("");
                }}
                sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 0.75 }}>
                Members
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={employeeOptions}
                value={form.employees}
                getOptionLabel={(o) => o.desc || ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                onChange={(_, value) => setForm((p) => ({ ...p, employees: value }))}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                      sx={{ mr: 1, color: "#9CA3AF", "&.Mui-checked": { color: RED } }}
                    />
                    {option.desc}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.desc}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.value}
                      sx={{ bgcolor: "rgba(191,29,45,0.08)", color: RED, fontWeight: 600 }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={form.employees.length ? "" : "Search and select members"}
                    size="small"
                  />
                )}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", mt: 1 }}>
                {form.employees.length} member{form.employees.length === 1 ? "" : "s"} selected
              </Typography>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeDialog}
            disabled={saving}
            sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || formLoading}
            sx={{
              bgcolor: RED,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 3,
              boxShadow: "none",
              "&:hover": { bgcolor: "#A81826", boxShadow: "none" },
            }}
          >
            {saving ? "Saving..." : dialogMode === "edit" ? "Save Changes" : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogContent sx={{ textAlign: "center", pt: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "rgba(220,38,38,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: "#DC2626", fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#111827", mb: 1 }}>
            Delete group?
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center", gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600, flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{
              bgcolor: "#DC2626",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              flex: 1,
              boxShadow: "none",
              "&:hover": { bgcolor: "#B91C1C", boxShadow: "none" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
